// src/components/ModalConsultaNfe.jsx
import React, { useEffect, useRef, useState } from 'react';
import { FaFileInvoice, FaSearch, FaCheckCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import {
  getFirestore,
  collection,
  addDoc,
  Timestamp,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  runTransaction,
  deleteDoc,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useEmpresaId } from '../context/EmpresaContext';

const db = getFirestore();
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

const formatMoney = (v) =>
  Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatQtd = (v) => Number(v).toString();

const onlyDigits = (s) => String(s || '').replace(/\D/g, '');
const formatCNPJ = (v) =>
  onlyDigits(v).replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*$/, '$1.$2.$3/$4-$5');

const tipoLabel = (t) =>
  ({
    ciencia: 'Ciência',
    confirmacao: 'Confirmação',
    desconhecimento: 'Desconhecimento',
    nao_realizada: 'Não realizada',
  }[String(t || '').toLowerCase()] || t);

function extractChave(value) {
  const fromUrl = value.match(/[?&]chNFe=([0-9]{44})/i)?.[1];
  if (fromUrl) return fromUrl;
  return value.replace(/\D/g, '');
}

function asArray(x) {
  if (!x) return [];
  return Array.isArray(x) ? x : [x];
}

function pickEmitente(data = {}) {
  const topNome = data.nome_emitente || data.emitente_nome;
  const topDoc = data.documento_emitente || data.cnpj_emitente || data.cpf_emitente;
  const emit = data.emitente || data.fornecedor || {};
  const nomeAninhado = emit.razao_social || emit.nome_fantasia || emit.nome || null;
  const docAninhado = emit.cnpj || emit.cpf || null;
  return { nome: topNome || nomeAninhado || null, doc: onlyDigits(topDoc || docAninhado || '') };
}

function pickValorTotal(data = {}) {
  const v1 = data.valor_total;
  const v2 = data?.total?.valor_total;
  const v3 = data?.nfe_completa?.total?.valor_total;
  const v4 = data?.nfe_completa?.NFe?.infNFe?.total?.ICMSTot?.vNF;
  const v5 = data?.nfe_completa?.ICMSTotal?.vNF;
  return Number(v1 ?? v2 ?? v3 ?? v4 ?? v5 ?? 0);
}

function extractItensFromData(data = {}) {
  const itensDiretos = data.itens || data.items || data?.requisicao_nota_fiscal?.itens;
  if (Array.isArray(itensDiretos) && itensDiretos.length) return itensDiretos;

  const detA = asArray(data?.nfe_completa?.det)
    .map((d) => d?.prod || null)
    .filter(Boolean);
  if (detA.length) {
    return detA.map((prod) => ({
      descricao: prod.xProd,
      xProd: prod.xProd,
      codigo_produto: prod.cProd,
      cProd: prod.cProd,
      codigo_ncm: prod.NCM,
      NCM: prod.NCM,
      unidade_comercial: prod.uCom,
      uCom: prod.uCom,
      quantidade_comercial: prod.qCom,
      qCom: prod.qCom,
      valor_unitario_comercial: prod.vUnCom,
      vUnCom: prod.vUnCom,
      informacoes_adicionais_item: prod.infAdProd || '',
      cfop: prod.CFOP,
      cest: prod.CEST,
    }));
  }

  const detB = asArray(data?.nfe_completa?.NFe?.infNFe?.det)
    .map((d) => d?.prod || null)
    .filter(Boolean);
  if (detB.length) {
    return detB.map((prod) => ({
      descricao: prod.xProd,
      xProd: prod.xProd,
      codigo_produto: prod.cProd,
      cProd: prod.cProd,
      codigo_ncm: prod.NCM,
      NCM: prod.NCM,
      unidade_comercial: prod.uCom,
      uCom: prod.uCom,
      quantidade_comercial: prod.qCom,
      qCom: prod.qCom,
      valor_unitario_comercial: prod.vUnCom,
      vUnCom: prod.vUnCom,
      informacoes_adicionais_item: prod.infAdProd || '',
      cfop: prod.CFOP,
      cest: prod.CEST,
    }));
  }
  return [];
}

const ModalConsultaNfe = ({ open, onClose, onImportarProdutos }) => {
  const empresaId = useEmpresaId();

  const [chaveNfe, setChaveNfe] = useState('');
  const [cnpjDest, setCnpjDest] = useState('');
  const [produtosNfe, setProdutosNfe] = useState([]);
  const [loadingNfe, setLoadingNfe] = useState(false);
  const [lucros, setLucros] = useState({});
  const [consultado, setConsultado] = useState(false);
  const [meta, setMeta] = useState(null);

  const [statusMsg, setStatusMsg] = useState('');
  const [polling, setPolling] = useState(false);
  const [pollTry, setPollTry] = useState(0);
  const pollPlan = [5, 10, 20, 40, 60, 120, 180];
  const pollTimerRef = useRef(null);
  const activeAbortRef = useRef(null);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        if (!empresaId) return;
        const snap = await getDoc(doc(db, 'empresas', empresaId));
        const cnpj = onlyDigits(snap.data()?.cnpj || '');
        if (!ignore && cnpj.length === 14) setCnpjDest(cnpj);
      } catch (_) {}
    })();
    return () => {
      ignore = true;
    };
  }, [empresaId]);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      if (activeAbortRef.current) activeAbortRef.current.abort();
    };
  }, []);

  async function consultarBackendPorChave(chave, cnpj) {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken(true);
    if (!token) throw new Error('Sem autenticação. Faça login novamente.');

    const url = new URL(`${API_URL}/api/focus/consultar-nfe-chave/${chave}`);
    if (cnpj) url.searchParams.set('cnpj', cnpj);
    if (empresaId) url.searchParams.set('empresaId', empresaId);
    url.searchParams.set('completa', '1');
    url.searchParams.set('_', Date.now().toString());

    if (activeAbortRef.current) activeAbortRef.current.abort();
    const ac = new AbortController();
    activeAbortRef.current = ac;

    const res = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      cache: 'no-store',
      signal: ac.signal,
    });

    if (!res.ok) {
      let msg = `Falha na consulta (${res.status}).`;
      try {
        const j = await res.json();
        if (j?.mensagem) msg = j.mensagem;
      } catch {
        try {
          msg = await res.text();
        } catch {}
      }
      throw new Error(msg || 'Falha na consulta.');
    }
    return res.json();
  }

  function applyConsulta(data, chaveFallback) {
    const emit = pickEmitente(data);
    const manifestacao =
      data.manifestacao_destinatario ||
      data.manifestacao ||
      data.manifestacaoDestinatario ||
      null;

    const chave = (data.chave_nfe || '').replace(/^NFe/, '') || chaveFallback;

    setMeta({
      nome_emitente: emit.nome,
      documento_emitente: emit.doc,
      situacao: data.situacao || data.status || null,
      manifestacao,
      nfe_completa: !!data.nfe_completa,
      pendente_xml: !!data.pendente_xml, // NOVO: flag vinda do backend, quando disponível
      chave,
      valor_total: pickValorTotal(data),
    });

    const itens = extractItensFromData(data);
    setProdutosNfe(Array.isArray(itens) ? itens : []);
    setConsultado(true);
    return { completa: !!data.nfe_completa, itens, pendenteXml: !!data.pendente_xml };
  }

  function startPolling(chave, cnpj) {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    setPolling(true);
    setPollTry(1);

    const tick = async (i) => {
      try {
        setPollTry(i + 1);
        const d = await consultarBackendPorChave(chave, cnpj);
        const { completa, itens } = applyConsulta(d, chave);
        if (completa && Array.isArray(itens) && itens.length) {
          setStatusMsg('');
          setPolling(false);
          Swal.fire({
            icon: 'success',
            title: 'XML completo liberado!',
            text: 'Carregamos os produtos desta NFe.',
            timer: 1600,
            showConfirmButton: false,
          });
          return;
        }
      } catch (e) {
        const m = String(e?.message || '').toLowerCase();
        if (
          m.includes('não autorizado') ||
          m.includes('http basic') ||
          m.includes('cadastrado')
        ) {
          setStatusMsg(e.message);
          setPolling(false);
          return;
        }
      }

      if (i < pollPlan.length - 1) {
        setStatusMsg('Aguardando o XML completo da SEFAZ/Focus…');
        pollTimerRef.current = setTimeout(() => tick(i + 1), pollPlan[i + 1] * 1000);
      } else {
        setPolling(false);
        setStatusMsg(
          'Ainda não recebemos o XML completo. Você pode tentar novamente ou ativar um aviso para ser notificado.'
        );
      }
    };

    setStatusMsg('Aguardando o XML completo da SEFAZ/Focus…');
    pollTimerRef.current = setTimeout(() => tick(0), pollPlan[0] * 1000);
  }

  async function ativarAviso() {
    try {
      if (!empresaId) throw new Error('Empresa não selecionada.');
      const chave = extractChave(chaveNfe).slice(0, 44);
      await addDoc(collection(db, 'empresas', empresaId, 'nfe_pendentes'), {
        chaveNfe: chave,
        criadoEm: Timestamp.now(),
        origem: 'consulta_modal',
      });
      Swal.fire({
        icon: 'success',
        title: 'Aviso ativado!',
        text: 'Vamos te avisar quando o XML completo estiver disponível.',
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire('Erro', e.message || 'Não foi possível ativar o aviso.', 'error');
    }
  }

  async function handleConsultarNfe() {
    const chave = extractChave(chaveNfe).slice(0, 44);
    const cnpj = onlyDigits(cnpjDest);

    if (!empresaId) return Swal.fire('Erro', 'Empresa não selecionada!', 'error');
    if (chave.length !== 44)
      return Swal.fire(
        'Chave incompleta',
        `A chave da NF-e precisa ter 44 dígitos. Você informou ${chave.length}.`,
        'warning'
      );

    setLoadingNfe(true);
    setProdutosNfe([]);
    setConsultado(false);
    setMeta(null);
    setStatusMsg('');
    setPolling(false);
    setPollTry(0);
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    if (activeAbortRef.current) activeAbortRef.current.abort();

    try {
      const impRef = doc(db, 'empresas', empresaId, 'nfes_importadas', chave);
      const ja = await getDoc(impRef);
      if (ja.exists()) {
        Swal.fire({
          icon: 'warning',
          title: 'Nota já importada!',
          text: 'Esta chave NFe já foi importada para seu estoque.',
        });
        setLoadingNfe(false);
        return;
      }
    } catch (err) {
      Swal.fire('Erro', 'Erro ao verificar NFe no banco.', 'error');
      setLoadingNfe(false);
      return;
    }

    try {
      const data = await consultarBackendPorChave(chave, cnpj);
      const { completa } = applyConsulta(data, chave);
      if (!completa) {
        const pendenteXml = !!data.pendente_xml;
        setStatusMsg(
          pendenteXml
            ? 'Encontramos a NFe, mas a SEFAZ/Focus ainda não liberou o XML completo. Vamos reconsultar automaticamente.'
            : 'Encontramos a NFe, vamos reconsultar para obter o XML completo.'
        );
        startPolling(chave, cnpj);
      }
    } catch (err) {
      Swal.fire('Erro', err.message || 'Erro de comunicação com o backend', 'error');
    } finally {
      setLoadingNfe(false);
    }
  }

  async function manifestar(tipo = 'ciencia') {
    const chave = extractChave(chaveNfe).slice(0, 44);
    const cnpj = onlyDigits(cnpjDest);
    if (!chave || chave.length !== 44)
      return Swal.fire('Atenção', 'Informe uma chave NFe válida (44 dígitos).', 'warning');
    if (cnpj.length !== 14)
      return Swal.fire('Atenção', 'Informe um CNPJ de destinatário válido (14 dígitos).', 'warning');

    try {
      setLoadingNfe(true);
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) throw new Error('Sem autenticação.');
      const url = new URL(`${API_URL}/api/focus/recebidas/${chave}/manifesto`);
      if (empresaId) url.searchParams.set('empresaId', empresaId);

      const res = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ tipo, cnpj }),
      });
      if (!res.ok) {
        let msg = `Falha ao manifestar (${res.status}).`;
        try {
          const j = await res.json();
          if (j?.mensagem) msg = j.mensagem;
        } catch {}
        throw new Error(msg);
      }
      const out = await res.json();
      setMeta((m) => ({ ...(m || {}), manifestacao: tipo, pendente_xml: !!out?.pendente_xml }));
      Swal.fire({
        icon: 'success',
        title: 'Manifesto enviado',
        text: out?.confirmado
          ? 'Confirmação detectada. Vamos reconsultar para buscar o XML completo.'
          : 'Vamos reconsultar a nota para tentar obter o XML completo.',
        timer: 1500,
        showConfirmButton: false,
      });
      setStatusMsg('Manifesto enviado. Aguardando atualização da SEFAZ/Focus…');
      startPolling(chave, cnpj);
    } catch (err) {
      Swal.fire('Erro', err.message || 'Falha ao manifestar.', 'error');
    } finally {
      setLoadingNfe(false);
    }
  }

  const handleLucroChange = (idx, value) => {
    setLucros((l) => ({ ...l, [idx]: value.replace(',', '.').replace(/[^0-9.]/g, '') }));
  };

  const importarProdutos = async () => {
    if (!empresaId) return Swal.fire('Erro', 'Empresa não selecionada!', 'error');

    const chave = extractChave(chaveNfe).slice(0, 44);
    const impRef = doc(db, 'empresas', empresaId, 'nfes_importadas', chave);
    let markerCriado = false;

    try {
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(impRef);
        if (snap.exists()) throw new Error('Esta NFe já foi importada para o seu estoque.');
        tx.set(impRef, { chaveNfe: chave, importadaEm: Timestamp.now(), origem: 'consulta_modal' });
      });
      markerCriado = true;

      for (let idx = 0; idx < produtosNfe.length; idx++) {
        const item = produtosNfe[idx];
        const precoBruto = Number(
          item.valor_unitario || item.vUnCom || item.valor_unitario_comercial || 0
        );
        const estoqueNovo = Number(
          item.quantidade_comercial || item.qCom || item.quantidade_tributavel || 0
        );
        const porcentagemLucro = Number(lucros[idx] || 0);
        const precoVenda = precoBruto * (1 + porcentagemLucro / 100);
        const sku = item.codigo_produto || item.cProd || '';
        const ncm = item.codigo_ncm || item.NCM || '';

        const camposFiscais = {
          cfop: item.cfop || '',
          ncm,
          cest: item.cest || '',
          icms_origem: item.icms_origem || '',
          icms_situacao_tributaria: item.icms_situacao_tributaria || '',
          icms_modalidade_base_calculo: item.icms_modalidade_base_calculo || '',
          icms_base_calculo: item.icms_base_calculo || '',
          icms_aliquota: item.icms_aliquota || '',
          icms_valor: item.icms_valor || '',
          ipi_situacao_tributaria: item.ipi_situacao_tributaria || '',
          pis_situacao_tributaria: item.pis_situicao_tributaria || item.pis_situacao_tributaria || '',
          pis_base_calculo: item.pis_base_calculo || '',
          pis_aliquota_porcentual: item.pis_aliquota_porcentual || '',
          pis_valor: item.pis_valor || '',
          cofins_situacao_tributaria:
            item.cofins_situicao_tributaria || item.cofins_situacao_tributaria || '',
          cofins_base_calculo: item.cofins_base_calculo || '',
          cofins_aliquota_porcentual: item.cofins_aliquota_porcentual || '',
          cofins_valor: item.cofins_valor || '',
        };

        const prodQ = query(
          collection(db, 'empresas', empresaId, 'produtos'),
          where('sku', '==', sku)
        );
        const prodSnap = await getDocs(prodQ);

        if (!prodSnap.empty) {
          const docRef = prodSnap.docs[0].ref;
          const produto = prodSnap.docs[0].data();
          const estoqueAtual = Number(produto.estoque) || 0;

          await updateDoc(docRef, {
            estoque: estoqueAtual + estoqueNovo,
            precoBruto,
            precoVenda: Number(precoVenda.toFixed(2)),
            porcentagemLucro,
            nome: item.descricao || item.xProd || 'Produto NFe',
            ncm,
            ...camposFiscais,
            descricao: item.informacoes_adicionais_item || '',
          });
        } else {
          await addDoc(collection(db, 'empresas', empresaId, 'produtos'), {
            nome: item.descricao || item.xProd || 'Produto NFe',
            precoBruto,
            estoque: estoqueNovo,
            unidade: item.unidade_comercial || item.uCom || 'UN',
            porcentagemLucro,
            precoVenda: Number(precoVenda.toFixed(2)),
            criadoEm: Timestamp.now(),
            sku,
            ncm,
            ...camposFiscais,
            descricao: item.informacoes_adicionais_item || '',
          });
        }
      }

      Swal.fire({
        icon: 'success',
        title: 'Produtos importados com sucesso!',
        timer: 1800,
        showConfirmButton: false,
      });
      handleClose();
      onImportarProdutos?.();
    } catch (err) {
      try {
        if (markerCriado) await deleteDoc(impRef);
      } catch (_) {}
      Swal.fire('Erro', err?.message || 'Falha ao importar produtos', 'error');
    }
  };

  async function abrirDanfeAutenticado(chave) {
    try {
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) throw new Error('Sem autenticação.');
      const url = new URL(`${API_URL}/api/focus/baixar-danfe/${chave}`);
      if (empresaId) url.searchParams.set('empresaId', empresaId);
      const r = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`Falha ao baixar DANFE (${r.status})`);
      const blob = await r.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.target = '_blank';
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 5000);
    } catch (e) {
      Swal.fire('Erro', e.message || 'Falha ao abrir DANFE.', 'error');
    }
  }

  async function baixarXmlAutenticado(chave) {
    try {
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) throw new Error('Sem autenticação.');
      const url = new URL(`${API_URL}/api/focus/baixar-xml/${chave}`);
      if (empresaId) url.searchParams.set('empresaId', empresaId);
      const r = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`Falha ao baixar XML (${r.status})`);
      const text = await r.text();
      const isResumo = /^\s*<\s*resNFe\b/i.test(text);
      if (isResumo) {
        Swal.fire({
          icon: 'info',
          title: 'XML de resumo (resNFe)',
          text: 'A SEFAZ/Focus ainda não liberou o XML completo. Tente “Consultar” novamente mais tarde.',
        });
      }
      const blob = new Blob([text], { type: 'application/xml' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${chave}.xml`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 5000);
    } catch (e) {
      Swal.fire('Erro', e.message || 'Falha ao baixar XML.', 'error');
    }
  }

  const handleClose = () => {
    onClose();
    setChaveNfe('');
    setProdutosNfe([]);
    setLucros({});
    setLoadingNfe(false);
    setConsultado(false);
    setMeta(null);
    setStatusMsg('');
    setPolling(false);
    setPollTry(0);
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    if (activeAbortRef.current) activeAbortRef.current.abort();
  };

  if (!open) return null;

  const chaveDisplay = extractChave(chaveNfe).slice(0, 44);
  const digitos = chaveDisplay.length;
  const nfeCompleta = !!meta?.nfe_completa;
  const chaveParaLinks = meta?.chave || chaveDisplay;
  const man = (meta?.manifestacao || '').toLowerCase();
  const isFinalManifest = ['confirmacao', 'desconhecimento', 'nao_realizada'].includes(man);
  const jaCiencia = man === 'ciencia' || isFinalManifest;

  const labelCiencia =
    isFinalManifest
      ? 'Manifestação final registrada'
      : man === 'ciencia'
      ? 'Ciência já registrada'
      : 'Dar ciência';
  const labelConfirmacao =
    man === 'confirmacao'
      ? 'Confirmação registrada'
      : isFinalManifest
      ? 'Manifestação final registrada'
      : 'Confirmar operação';
  const labelDesc =
    man === 'desconhecimento'
      ? 'Desconhecimento registrado'
      : isFinalManifest
      ? 'Manifestação final registrada'
      : 'Desconheço';
  const labelNaoReal =
    man === 'nao_realizada'
      ? 'Não realizada registrada'
      : isFinalManifest
      ? 'Manifestação final registrada'
      : 'Não realizada';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein">
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-6xl relative animate-modalSlideIn max-h-[90vh] flex flex-col">
        {/* HEADER (inputs e ações principais) */}
        <div className="relative px-10 pt-10 pb-4 border-b dark:border-gray-800">
          <button
            className="absolute right-8 top-7 text-gray-500 hover:text-[#004072] dark:text-gray-200 dark:hover:text-white text-base font-medium"
            onClick={handleClose}
          >
            Fechar
          </button>

          <h2 className="text-2xl font-bold mb-1 text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <FaFileInvoice className="text-[#004AAD]" /> Consultar NFe pela Chave
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-300 mb-3">
            Cole a <b>chave (44 dígitos)</b> da NF-e.
          </p>
          <details className="mb-3 rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2 text-[13px] text-blue-900 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-100">
  <summary className="cursor-pointer select-none">Como funciona esta consulta?</summary>
  <div className="mt-2">
    Informe a chave de uma NFe <b>recebida</b> pelo <b>CNPJ cadastrado</b>.
    Assim que o <b>XML completo</b> estiver disponível, você pode <b>importar os produtos </b>
    direto para o estoque. Pode levar alguns minutos após a manifestação.
  </div>
</details>


          <div className="flex gap-2 mb-2">
            <input
              type="text"
              inputMode="numeric"
              placeholder="Chave da NFe (44 dígitos)"
              className="border px-3 py-2 rounded-md w-full bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              value={chaveDisplay}
              onChange={(e) => setChaveNfe(extractChave(e.target.value).slice(0, 44))}
              onPaste={(e) => {
                const txt = e.clipboardData.getData('text');
                const chave = extractChave(txt).slice(0, 44);
                if (chave) {
                  e.preventDefault();
                  setChaveNfe(chave);
                }
              }}
              maxLength={44}
            />
            <button
              onClick={handleConsultarNfe}
              disabled={loadingNfe || !chaveDisplay}
              className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 font-semibold text-white
                         hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2
                         transition disabled:opacity-60"
              title="Consultar NFe"
            >
              <FaSearch /> Consultar
            </button>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 dark:text-gray-400">{digitos}/44 dígitos</span>
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600 dark:text-gray-300">CNPJ destinatário:</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="CNPJ (14 dígitos)"
                className="border px-2 py-1 rounded-md w-56 bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white text-sm"
                value={formatCNPJ(cnpjDest)}
                onChange={(e) => setCnpjDest(onlyDigits(e.target.value).slice(0, 14))}
                maxLength={18}
                title="CNPJ da sua empresa (destinatário)"
              />
            </div>
          </div>
        </div>

        {/* BODY SCROLLABLE */}
        <div className="flex-1 min-h-0 overflow-y-auto px-10 pb-20">
          {statusMsg && (
            <div className="mt-4 mb-4 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-600">
              {statusMsg}
              {polling && (
                <span className="ml-2 animate-pulse">
                  (tentativa {pollTry}/{pollPlan.length})
                </span>
              )}
              {!polling && (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={ativarAviso}
                    className="text-xs px-3 py-1 rounded-full bg-amber-600 text-white hover:bg-amber-700"
                  >
                    Ativar aviso
                  </button>
                  <button
                    onClick={() =>
                      startPolling(extractChave(chaveNfe).slice(0, 44), onlyDigits(cnpjDest))
                    }
                    className="text-xs px-3 py-1 rounded-full border border-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                  >
                    Tentar novamente
                  </button>
                </div>
              )}
            </div>
          )}

          {meta && (
            <div className="rounded-xl border dark:border-gray-700 p-4 mb-5 bg-gray-50 dark:bg-gray-800/50 text-sm">
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <div>
                  <b>Emitente:</b> {meta.nome_emitente} ({meta.documento_emitente})
                </div>
                <div>
                  <b>Situação:</b> {meta.situacao || '—'}
                </div>
                <div>
                  <b>Manifestação:</b> {tipoLabel(meta.manifestacao) || '—'}
                </div>
                <div>
                  <b>XML completo?</b> {meta.nfe_completa ? 'Sim' : 'Não'}
                </div>
                {meta?.valor_total != null && (
                  <div>
                    <b>Valor:</b> R$ {formatMoney(meta.valor_total)}
                  </div>
                )}
                {!meta.nfe_completa && typeof meta.pendente_xml === 'boolean' && (
                  <div>
                    <b>Pendente XML:</b> {meta.pendente_xml ? 'Sim' : 'Não'}
                  </div>
                )}
              </div>
            </div>
          )}

          {meta && !nfeCompleta && isFinalManifest && (
            <div className="mb-4 rounded-lg border border-blue-300 bg-blue-50 text-blue-900 px-4 py-3 text-sm dark:bg-blue-900/20 dark:text-blue-200 dark:border-blue-600">
              Manifestação <b>final</b> ({tipoLabel(man)}) já registrada para esta NFe. A SEFAZ/Focus
              pode levar alguns minutos para liberar o XML completo. Aguarde e clique em{' '}
              <b>Consultar</b> novamente.
            </div>
          )}

          {meta && !nfeCompleta && !isFinalManifest && man === 'ciencia' && (
            <div className="mb-4 rounded-lg border border-blue-300 bg-blue-50 text-blue-900 px-4 py-3 text-sm dark:bg-blue-900/20 dark:text-blue-200 dark:border-blue-600">
              Ciência da operação <b>já registrada</b>. Aguarde alguns minutos e clique em{' '}
              <b>Consultar</b> novamente.
            </div>
          )}

          {meta && !nfeCompleta && (
            <div className="mb-6 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200 text-sm">
              Recebemos apenas o <b>cabeçalho</b> desta NFe. Registre uma <b>manifestação</b> para
              liberar os detalhes.
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={() => manifestar('ciencia')}
                  disabled={loadingNfe || jaCiencia}
                  className="px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60"
                >
                  {labelCiencia}
                </button>
                <button
                  onClick={() => manifestar('confirmacao')}
                  disabled={loadingNfe || isFinalManifest || man === 'confirmacao'}
                  className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {labelConfirmacao}
                </button>
                <button
                  onClick={() => manifestar('desconhecimento')}
                  disabled={loadingNfe || isFinalManifest || man === 'desconhecimento'}
                  className="px-3 py-1.5 rounded-lg bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-60"
                >
                  {labelDesc}
                </button>
                <button
                  onClick={() => manifestar('nao_realizada')}
                  disabled={loadingNfe || isFinalManifest || man === 'nao_realizada'}
                  className="px-3 py-1.5 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
                >
                  {labelNaoReal}
                </button>
              </div>
            </div>
          )}

          {meta && nfeCompleta && (
            <div className="mb-4 flex flex-wrap gap-3">
              <button
                className="px-3 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-800"
                onClick={() => baixarXmlAutenticado(chaveParaLinks)}
              >
                Baixar XML
              </button>
              <button
                className="px-3 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-800"
                onClick={() => abrirDanfeAutenticado(chaveParaLinks)}
              >
                Abrir DANFe
              </button>
            </div>
          )}

          {loadingNfe ? (
            <p className="text-center dark:text-white mt-4">Processando…</p>
          ) : produtosNfe.length > 0 ? (
            <>
              <h3 className="font-semibold text-lg mb-2 dark:text-white">Produtos encontrados:</h3>

              {/* SCROLL DA TABELA */}
              <div className="overflow-x-auto rounded-lg border dark:border-gray-800">
                <div className="max-h-[52vh] overflow-auto">
                  <table className="min-w-full table-fixed bg-white dark:bg-gray-900 text-sm">
                    <thead className="sticky top-0 bg-white dark:bg-gray-900 shadow-sm">
                      <tr>
                        <th className="p-2 text-left font-semibold w-[32%]">Nome</th>
                        <th className="p-2 text-center font-semibold w-[14%]">SKU</th>
                        <th className="p-2 text-center font-semibold w-[12%]">NCM</th>
                        <th className="p-2 text-center font-semibold w-[10%]">Qtd.</th>
                        <th className="p-2 text-center font-semibold w-[14%]">Preço Bruto</th>
                        <th className="p-2 text-center font-semibold w-[10%]">% Lucro</th>
                        <th className="p-2 text-right font-semibold w-[14%]">Venda</th>
                      </tr>
                    </thead>
                    <tbody>
                      {produtosNfe.map((item, idx) => {
                        const precoBruto = Number(
                          item.valor_unitario || item.vUnCom || item.valor_unitario_comercial || 0
                        );
                        const lucro = Number(lucros[idx] || 0);
                        const precoVenda = precoBruto * (1 + lucro / 100);
                        const qtd = formatQtd(
                          item.quantidade_comercial || item.qCom || item.quantidade_tributavel || 0
                        );
                        const sku = item.codigo_produto || item.cProd || '';
                        const ncm = item.codigo_ncm || item.NCM || '';
                        return (
                          <tr key={idx} className="odd:bg-gray-50/60 dark:odd:bg-gray-800/40">
                            <td className="p-2 align-middle max-w-[360px] whitespace-normal break-words">
                              {item.descricao || item.xProd || ''}
                            </td>
                            <td className="p-2 text-center align-middle break-words">{sku}</td>
                            <td className="p-2 text-center align-middle">{ncm}</td>
                            <td className="p-2 text-center align-middle">{qtd}</td>
                            <td className="p-2 text-center align-middle">R$ {formatMoney(precoBruto)}</td>
                            <td className="p-2 text-center align-middle">
                              <input
                                type="number"
                                className="border w-20 px-2 py-1 rounded-md text-center"
                                value={lucros[idx] || ''}
                                min={0}
                                max={500}
                                step="0.01"
                                onChange={(e) => handleLucroChange(idx, e.target.value)}
                                placeholder="0"
                              />
                            </td>
                            <td className="p-2 text-right align-middle font-semibold text-green-700 dark:text-green-400 whitespace-nowrap">
                              R$ {formatMoney(precoVenda)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : consultado ? (
            <p className="text-center dark:text-white mt-6">
              Nenhum produto encontrado para esta chave NFe.
              {!nfeCompleta && ' (parece ser apenas o cabeçalho – registre a manifestação acima)'}
            </p>
          ) : null}
        </div>

        {/* FOOTER FIXO (botão nunca some) */}
        {produtosNfe.length > 0 && (
          <div className="px-10 py-4 border-t dark:border-gray-800 bg-white dark:bg-gray-900">
            <button
              className="bg-[#004AAD] text-white px-8 py-3 rounded-full flex items-center gap-3 mx-auto text-lg font-semibold hover:bg-[#003666] transition-all shadow-lg w-full sm:w-auto"
              onClick={importarProdutos}
              disabled={produtosNfe.length === 0}
            >
              <FaCheckCircle /> Importar todos para o estoque
            </button>
          </div>
        )}
      </div>

      <style>{`
        .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
        @keyframes modalSlideIn { 0% { opacity: 0; transform: translateY(60px) scale(.97);} 100% { opacity: 1; transform: translateY(0) scale(1);} }
        .animate-fadein { animation: fadein 0.25s; }
        @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
};

export default ModalConsultaNfe;
